import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinSearchAccordionComponent } from '../../shared/design-system/search-accordion.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { FinanceApiService, PlatformCustomerRecord } from './finance-api.service';

type StatusFilterOption = { value: number | null; label: string };

@Component({
  standalone: true,
  selector: 'ain-finance-customers',
  imports: [
    AinPageComponent,
    AinSearchAccordionComponent,
    AinStateComponent,
    InfoHintComponent,
    RouterLink,
    FormsModule,
    TableModule,
    ButtonModule,
    SelectModule
  ],
  template: `
    <ain-page [title]="t('finance.customers.pageTitle')" [description]="t('finance.customers.pageDescription')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'partyHub.help.customersList.title'"
        [descriptionKey]="'partyHub.help.customersList.description'"
        [bulletKeys]="[
          'partyHub.help.customersList.bullets.partyColumn',
          'partyHub.help.customersList.bullets.onboard',
          'partyHub.help.customersList.bullets.status'
        ]" />

      <div page-actions>
        <button pButton type="button" [label]="t('common.create')" (click)="openCreate()"></button>
      </div>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <ain-search-accordion [title]="t('platform.search.title')">
          <section class="search-grid">
            <input [(ngModel)]="filters.query" [placeholder]="t('finance.customers.searchPlaceholder')" />
            <p-select
              class="status-select"
              [(ngModel)]="filters.status"
              [options]="statusFilterOptions()"
              optionLabel="label"
              optionValue="value"
              [showClear]="true"
              [placeholder]="t('common.all')"
              appendTo="body" />
            <button pButton type="button" [label]="t('platform.search.apply')" (click)="load()"></button>
            <button pButton type="button" severity="secondary" [label]="t('platform.search.clear')" (click)="clearFilters()"></button>
          </section>
        </ain-search-accordion>

        @if (!rows().length) {
          <ain-state type="empty" [title]="t('common.noRecords')" />
        } @else {
          <p-table [value]="rows()">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('finance.customers.fields.customerCode') }}</th>
                <th>{{ t('finance.customers.fields.displayName') }}</th>
                <th>{{ t('partyHub.columns.party') }}</th>
                <th>{{ t('finance.customers.fields.type') }}</th>
                <th>{{ t('columns.status') }}</th>
                <th>{{ t('finance.customers.fields.defaultCurrencyCode') }}</th>
                <th>{{ t('finance.customers.fields.actions') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.customerCode }}</td>
                <td>{{ row.displayName }}</td>
                <td>
                  @if (row.party?.id) {
                    <a class="row-link" [routerLink]="['/platform/parties', row.party.id]">{{ row.party.displayName }}</a>
                  } @else if (row.partyId) {
                    <a class="row-link" [routerLink]="['/platform/parties', row.partyId]">{{ t('partyHub.actions.openParty') }}</a>
                  } @else {
                    —
                  }
                </td>
                <td>{{ typeLabel(row.type) }}</td>
                <td>{{ statusLabel(row.status) }}</td>
                <td>{{ row.defaultCurrencyCode }}</td>
                <td class="actions">
                  <button pButton type="button" size="small" [label]="t('common.edit')" text="true" (click)="openDetail(row.id)"></button>
                  <button pButton type="button" size="small" [label]="t('finance.customers.actions.activate')" text="true" severity="success" [disabled]="isStatus(row, 1)" (click)="activate(row.id)"></button>
                  <button pButton type="button" size="small" [label]="t('finance.customers.actions.suspend')" text="true" severity="warn" [disabled]="isStatus(row, 2)" (click)="suspend(row.id)"></button>
                  <button pButton type="button" size="small" [label]="t('finance.customers.actions.close')" text="true" severity="danger" [disabled]="isStatus(row, 3)" (click)="close(row.id)"></button>
                </td>
              </tr>
            </ng-template>
          </p-table>
        }
      }

      @if (errorMessage()) {
        <p class="message error">{{ errorMessage() }}</p>
      }
    </ain-page>
  `,
  styles: [`
    .search-grid { display: grid; grid-template-columns: 2fr 1fr auto auto; gap: 8px; align-items: center; }
    .search-grid input { height: 38px; border: 1px solid #cbd5e1; border-radius: 8px; padding: 0 10px; width: 100%; }
    .status-select { width: 100%; }
    .actions { display: flex; flex-wrap: wrap; gap: 4px; }
    .row-link { color: var(--ain-color-primary); font-weight: 600; text-decoration: none; }
    .message { margin-top: 10px; }
    .error { color: #b91c1c; }
    @media (max-width: 1200px) { .search-grid { grid-template-columns: 1fr; } }
  `]
})
export class FinanceCustomersComponent implements OnInit {
  private readonly api = inject(FinanceApiService);
  private readonly localization = inject(LocalizationService);
  private readonly router = inject(Router);

  readonly loading = signal(false);
  readonly rows = signal<PlatformCustomerRecord[]>([]);
  readonly errorMessage = signal('');
  readonly total = signal(0);
  readonly canLoad = computed(() => !this.loading());

  filters = {
    query: '',
    status: null as number | null
  };

  ngOnInit(): void {
    this.load();
  }

  statusFilterOptions(): StatusFilterOption[] {
    return [
      { value: 0, label: this.statusLabel(0) },
      { value: 1, label: this.statusLabel(1) },
      { value: 2, label: this.statusLabel(2) },
      { value: 3, label: this.statusLabel(3) }
    ];
  }

  load(): void {
    if (!this.canLoad()) {
      return;
    }

    this.loading.set(true);
    this.errorMessage.set('');
    this.api.listPlatformCustomers({
      query: this.filters.query,
      status: this.filters.status === null ? '' : String(this.filters.status),
      skip: 0,
      take: 200
    }).subscribe({
      next: response => {
        this.rows.set(response.items);
        this.total.set(response.total);
        this.loading.set(false);
      },
      error: () => {
        this.rows.set([]);
        this.total.set(0);
        this.errorMessage.set(this.t('common.loadFailed'));
        this.loading.set(false);
      }
    });
  }

  clearFilters(): void {
    this.filters.query = '';
    this.filters.status = null;
    this.load();
  }

  openCreate(): void {
    void this.router.navigate(['/financial/payments/customers/new']);
  }

  openDetail(customerId: string): void {
    void this.router.navigate(['/financial/payments/customers', customerId]);
  }

  activate(customerId: string): void {
    this.api.activatePlatformCustomer(customerId).subscribe({
      next: () => this.load(),
      error: () => this.errorMessage.set(this.t('common.saveFailed'))
    });
  }

  suspend(customerId: string): void {
    this.api.suspendPlatformCustomer(customerId).subscribe({
      next: () => this.load(),
      error: () => this.errorMessage.set(this.t('common.saveFailed'))
    });
  }

  close(customerId: string): void {
    this.api.closePlatformCustomer(customerId).subscribe({
      next: () => this.load(),
      error: () => this.errorMessage.set(this.t('common.saveFailed'))
    });
  }

  isStatus(customer: PlatformCustomerRecord, value: number): boolean {
    const current = typeof customer.status === 'string' ? Number(customer.status) : customer.status;
    return current === value;
  }

  statusLabel(value: number | string): string {
    const numeric = typeof value === 'string' ? Number(value) : value;
    const map: Record<number, string> = {
      0: this.t('finance.customers.status.draft'),
      1: this.t('finance.customers.status.active'),
      2: this.t('finance.customers.status.suspended'),
      3: this.t('finance.customers.status.closed')
    };
    return map[numeric] ?? `${value}`;
  }

  typeLabel(value: number | string): string {
    const numeric = typeof value === 'string' ? Number(value) : value;
    const map: Record<number, string> = {
      0: this.t('finance.customers.type.b2c'),
      1: this.t('finance.customers.type.b2b'),
      2: this.t('finance.customers.type.government'),
      3: this.t('finance.customers.type.dealer'),
      4: this.t('finance.customers.type.distributor'),
      5: this.t('finance.customers.type.broker'),
      6: this.t('finance.customers.type.insured'),
      7: this.t('finance.customers.type.fxClient'),
      8: this.t('finance.customers.type.other')
    };
    return map[numeric] ?? `${value}`;
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
